import { CANVAS_ENTITY_CLASS_NAME } from '@/lib/CanvasEngine/const'
import type { ILine, IRenderer, PointerEventHandler } from '@/lib/CanvasEngine/backend/IRenderer'

/**
 * Balloon Connected Line 설정 인터페이스
 */
export interface BalloonConnectedLineConfig {
  color: string
  strokeWidth: number
  isTransparent?: boolean
}

/**
 * Balloon Connected Line 클래스
 * Balloon과 Shape을 연결하는 선을 관리
 */
export class BalloonConnectedLine {
  public line: ILine
  private config: BalloonConnectedLineConfig

  constructor(
    private readonly renderer: IRenderer,
    config: BalloonConnectedLineConfig,
  ) {
    this.config = config
    this.line = this.createLine()
  }

  /**
   * Connected Line 생성
   */
  private createLine(): ILine {
    const { color, strokeWidth, isTransparent = false } = this.config

    return (
      this.renderer
        .createLine({
          points: [0, 0, 0, 0],
          stroke: isTransparent ? 'transparent' : color,
          fill: color,
          strokeWidth: isTransparent ? 0 : strokeWidth,
          hitArea: { kind: 'strokeWidth', px: 10 },
          visible: !isTransparent,
          listening: false, // connectedLine은 클릭 이벤트를 받지 않음 (shape 클릭 방해 방지)
        })
        // 표식 속성(읽는 곳 없음) — 생성 config와 같은 attrs를 남긴다
        .setAttr('className', CANVAS_ENTITY_CLASS_NAME.READONLY)
    )
  }

  /**
   * Line Points 업데이트
   */
  public updatePoints(points: number[]): void {
    this.line.points(points)
  }

  /**
   * Line 색상 변경
   */
  public changeColor(color: string): void {
    if (!this.config.isTransparent) {
      this.line.stroke(color)
      this.line.fill(color)
    }
    this.config.color = color
  }

  /**
   * Line 표시/숨김
   */
  public setVisible(visible: boolean): void {
    if (!this.config.isTransparent) {
      this.line.visible(visible)
    }
  }

  /**
   * 레이어 순서 변경 (맨 아래로)
   */
  public moveToBottom(): void {
    this.line.moveToBottom()
  }

  /**
   * 이벤트 리스너 등록
   */
  public on(eventName: string, handler: PointerEventHandler): void {
    if (!this.config.isTransparent) {
      this.line.on(eventName, handler)
    }
  }

  /**
   * Line이 투명한지 확인
   */
  public isTransparent(): boolean {
    return this.config.isTransparent || false
  }

  /**
   * 현재 Points 가져오기
   */
  public getPoints(): number[] {
    return this.line.points()
  }
}
